import { useMemo, useState } from "react";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/lilia-ui/dialog";
import { Button } from "@/components/lilia-ui/button";
import { IconTrash, IconArrowLeft, IconArrowRight, IconArrowUp, IconArrowDown } from "@tabler/icons-react";
import { BlockEditModal } from "../BlockEditModal";
import { renderMarkersToHtml } from "@/lib/render-markers";
import styles from "../blocks/blocks.module.scss";
import { LabelChip } from "../LabelChip";
import { FullWidthSwitch } from "../FullWidthSwitch";
import { deriveLabel } from "@/lib/labels/labelKeys";
import { documentLabels } from "@/lib/labels/documentLabels";
import { cellSpan, cellText, coveredCells, withText } from "@/lib/tables/cells";
import { testIds } from "@/testIds";
import { toCells, type LooseCell } from "../table-grid/tableCells";
import { rowColorsFromBlock, rowColour } from "../table-grid/tableModel";
import { headerWithUnit, planColumns, type Best } from "../table-grid/tableFormat";
import { DecimalNumber, planCell } from "../table-grid/DecimalNumber";
import {
  groupStarts, noteMarks, remapGroups, rowGroupsAfterDelete, validGroups, type HeaderGroup,
} from "../table-grid/tableLayout";
import { GroupHeaderRow, NoteMark, TableNotes, noteMarkHtml } from "../table-grid/TableLayoutParts";
import { useApiContext } from "@/contexts/ApiContext";
import { useEditorStore } from "@/store/editorStore";
import { blockToTableData, type TableBlockContent } from "../table-grid/tableModel";
import { defaultSpec, docTables, isPlotProblem, problemText, tableName as nameOfTable } from "@/lib/tikzPlot";
import { PlotDialog } from "../plot/PlotDialog";

type ColAlign = "l" | "c" | "r" | "p" | "decimal";
const COL_ALIGN_OPTIONS: Array<{ value: ColAlign; label: string }> = [
  { value: "l", label: "Left" },
  { value: "c", label: "Center" },
  { value: "r", label: "Right" },
  { value: "decimal", label: "Decimal point" },
  { value: "p", label: "Wrap (set width)" },
];

export function TableNodeView({ node, updateAttributes, selected, deleteNode, editor, getPos }: NodeViewProps) {
  // Cells are strings or {content, colspan, rowspan} objects (imports, merged
  // cells) — read through lib/tables/cells, as the exporters read them.
  const headers = (node.attrs.headers as unknown[]) || ["Column 1", "Column 2", "Column 3"];
  const rows = (node.attrs.rows as unknown[][]) || [["", "", ""], ["", "", ""]];
  const tableCaption = (node.attrs.caption as string) || "";
  const tableLabel = (node.attrs.label as string) || "";
  const tableShortCaption = (node.attrs.shortCaption as string) || "";
  // The table's own row colours live in what the editor carries, not in its attributes.
  const ownRows = rowColorsFromBlock((node.attrs.carry as { content?: { rowColors?: unknown } } | null)?.content?.rowColors);
  const tableColumnAlign = (node.attrs.columnAlign as string[]) || [];
  const tableColumnWidth = (node.attrs.columnWidth as string[]) || [];
  // The columns' meaning (2g), as the PDF prints it: decimal alignment (chosen,
  // or automatic for a column of numbers), the unit in the header, bold best.
  const tableColumnUnit = (node.attrs.columnUnit as (string | null)[] | null) || undefined;
  const tableColumnBest = (node.attrs.columnBest as (Best | null)[] | null) || undefined;
  const width = Math.max(headers.length, ...rows.map((r) => r.length));
  const plan = useMemo(
    () => planColumns({
      hasHeader: true,
      align: tableColumnAlign,
      header: toCells(headers as LooseCell[]),
      rows: rows.map((r) => toCells(r as LooseCell[])),
      units: tableColumnUnit,
      best: tableColumnBest,
    }, width),
    [headers, rows, tableColumnAlign, tableColumnUnit, tableColumnBest, width],
  );
  // The rest of the table's meaning (2g): group headers, notes, row groups.
  const tableGroups = (node.attrs.headerGroups as HeaderGroup[] | null) || undefined;
  const tableRowGroups = (node.attrs.rowGroups as number[] | null) || undefined;
  const layout = useMemo(() => {
    const t = { hasHeader: true, header: toCells(headers as LooseCell[]), rows: rows.map((r) => toCells(r as LooseCell[])), groups: tableGroups, rowGroups: tableRowGroups };
    return { groups: validGroups(t, width), starts: groupStarts(t), ...noteMarks(t) };
  }, [headers, rows, tableGroups, tableRowGroups, width]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [draftHeaders, setDraftHeaders] = useState<unknown[]>(headers);
  const [draftRows, setDraftRows] = useState<unknown[][]>(rows);
  const [draftCaption, setDraftCaption] = useState(tableCaption);
  const [draftLabel, setDraftLabel] = useState(tableLabel);
  const [draftShortCaption, setDraftShortCaption] = useState(tableShortCaption);
  const [draftFullWidth, setDraftFullWidth] = useState(node.attrs.span === "page");
  const [draftColumnAlign, setDraftColumnAlign] = useState<string[]>(tableColumnAlign);
  const [draftColumnWidth, setDraftColumnWidth] = useState<string[]>(tableColumnWidth);
  // Kept in step with column removal and addition so a unit or a best choice
  // stays on its column.
  const [draftColumnUnit, setDraftColumnUnit] = useState<(string | null)[] | undefined>(tableColumnUnit);
  const [draftColumnBest, setDraftColumnBest] = useState<(Best | null)[] | undefined>(tableColumnBest);
  // Group headers and row groups stay on their columns and rows as they are removed.
  const [draftGroups, setDraftGroups] = useState<HeaderGroup[] | undefined>(tableGroups);
  const [draftRowGroups, setDraftRowGroups] = useState<number[] | undefined>(tableRowGroups);

  const handleOpenModal = () => {
    setDraftHeaders([...headers]);
    setDraftRows(rows.map(row => [...row]));
    setDraftCaption(tableCaption);
    setDraftLabel(tableLabel);
    setDraftShortCaption(tableShortCaption);
    setDraftFullWidth(node.attrs.span === "page");
    setDraftColumnAlign([...tableColumnAlign]);
    setDraftColumnWidth([...tableColumnWidth]);
    setDraftColumnUnit(tableColumnUnit && [...tableColumnUnit]);
    setDraftColumnBest(tableColumnBest && [...tableColumnBest]);
    setDraftGroups(tableGroups && tableGroups.map((g) => ({ ...g })));
    setDraftRowGroups(tableRowGroups && [...tableRowGroups]);
    setIsModalOpen(true);
  };

  // Every other label in the document, live — including ones not yet saved.
  const pos = typeof getPos === "function" ? getPos() : undefined;
  const otherLabels = isModalOpen ? documentLabels(editor.state.doc, pos) : [];
  // What the caption would give this table, offered only while it has no label.
  const derivedLabel = draftLabel
    ? null
    : deriveLabel(draftCaption, "table", new Set(otherLabels.map((o) => o.key)));

  const handleSave = () => {
    updateAttributes({
      headers: draftHeaders,
      rows: draftRows,
      caption: draftCaption,
      // Derived only when the table has no label. Once it has one, renaming the
      // caption leaves it alone — something may already point at it.
      label: draftLabel || derivedLabel || "",
      shortCaption: draftShortCaption,
      span: draftFullWidth ? "page" : null,
      columnAlign: draftColumnAlign,
      columnWidth: draftColumnWidth,
      columnUnit: draftColumnUnit?.some((u) => u) ? draftColumnUnit : null,
      columnBest: draftColumnBest?.some((b) => b) ? draftColumnBest : null,
      headerGroups: draftGroups?.length ? draftGroups : null,
      rowGroups: draftRowGroups?.length ? draftRowGroups : null,
    });
    setIsModalOpen(false);
  };

  const setColumnAlign = (colIdx: number, value: string) => {
    const next = [...draftColumnAlign];
    while (next.length <= colIdx) next.push("l");
    next[colIdx] = value;
    setDraftColumnAlign(next);
  };
  const setColumnWidth = (colIdx: number, value: string) => {
    const next = [...draftColumnWidth];
    while (next.length <= colIdx) next.push("");
    next[colIdx] = value;
    setDraftColumnWidth(next);
  };

  const handleHeaderChange = (index: number, value: string) => {
    const newHeaders = [...draftHeaders];
    newHeaders[index] = withText(newHeaders[index], value);
    setDraftHeaders(newHeaders);
  };

  const handleCellChange = (rowIndex: number, colIndex: number, value: string) => {
    const newRows = draftRows.map((row, ri) =>
      // withText keeps a merged cell's span; a plain string would drop it.
      ri === rowIndex ? row.map((cell, ci) => (ci === colIndex ? withText(cell, value) : cell)) : row
    );
    setDraftRows(newRows);
  };

  const addRow = () => setDraftRows([...draftRows, new Array(draftHeaders.length).fill("")]);
  const addColumn = () => {
    setDraftHeaders([...draftHeaders, `Column ${draftHeaders.length + 1}`]);
    setDraftRows(draftRows.map(row => [...row, ""]));
    // Keep parallel arrays aligned so the Phase-2a column-spec data
    // doesn't drift one slot off.
    setDraftColumnAlign([...draftColumnAlign, "l"]);
    setDraftColumnWidth([...draftColumnWidth, ""]);
    if (draftColumnUnit) setDraftColumnUnit([...draftColumnUnit, null]);
    if (draftColumnBest) setDraftColumnBest([...draftColumnBest, null]);
  };

  const removeRow = (index: number) => {
    if (draftRows.length <= 1) return;
    setDraftRows(draftRows.filter((_, i) => i !== index));
    setDraftRowGroups(rowGroupsAfterDelete(draftRowGroups, index, draftRows.length - 1));
  };

  const removeColumn = (index: number) => {
    if (draftHeaders.length <= 1) return;
    setDraftHeaders(draftHeaders.filter((_, i) => i !== index));
    setDraftRows(draftRows.map(row => row.filter((_, i) => i !== index)));
    setDraftColumnAlign(draftColumnAlign.filter((_, i) => i !== index));
    setDraftColumnWidth(draftColumnWidth.filter((_, i) => i !== index));
    if (draftColumnUnit) setDraftColumnUnit(draftColumnUnit.filter((_, i) => i !== index));
    if (draftColumnBest) setDraftColumnBest(draftColumnBest.filter((_, i) => i !== index));
    setDraftGroups(remapGroups(draftGroups, (old) => (old < index ? old : old === index ? null : old - 1)));
  };

  const covered = coveredCells(rows);

  // Plot this table (TikZ step 4a): the dialog on this table; the plot goes right after it.
  const { fetchWithAuth } = useApiContext();
  const documentId = useEditorStore((s) => s.documentId);
  const [plotOpen, setPlotOpen] = useState(false);
  const [showPlotReason, setShowPlotReason] = useState(false);
  const tableBlockId = (node.attrs.blockId as string) || "";
  const plotTable = useMemo(() => blockToTableData(node.attrs as TableBlockContent), [node.attrs]);
  const plotName = editor?.state ? nameOfTable(docTables(editor.state.doc), tableBlockId) : "a table";
  const suggested = useMemo(() => defaultSpec(plotTable, tableBlockId), [plotTable, tableBlockId]);
  const cantPlot = isPlotProblem(suggested) ? problemText(suggested, plotName === "a table" ? "This table" : plotName) : null;

  return (
    <NodeViewWrapper data-type="table-block" data-testid={testIds.table.block} data-block-id={node.attrs.blockId} className={selected ? "ProseMirror-selectednode" : ""}>
      <div className={styles.tableBlock}>
        {tableCaption && (
          <div style={{ fontSize: 13, fontWeight: 500, textAlign: "center", marginBottom: 4, color: "var(--lilia-text, #334155)" }}>
            {tableCaption}
          </div>
        )}
        <table className={styles.table} onClick={handleOpenModal} title="Click to edit table" style={{ cursor: "pointer" }}>
          <thead>
            <GroupHeaderRow groups={layout.groups} width={width} />
            <tr>
              {headers.map((header, index) => (
                <th
                  key={index}
                  data-testid={testIds.table.headerCell}
                  data-col={index}
                  style={plan[index]?.decimal ? { textAlign: "center" } : undefined}
                  dangerouslySetInnerHTML={{ __html: renderMarkersToHtml(headerWithUnit(cellText(header), plan[index]?.unit)) + noteMarkHtml(layout.marks.get(`-1:${index}`)) }}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              // A row group's rule above its first row (2g).
              <tr key={rowIndex} data-group-start={layout.starts.has(rowIndex) || undefined} style={{ ...(layout.starts.has(rowIndex) ? { borderTop: "1.5px solid var(--lilia-text, #334155)" } : {}), background: rowColour(ownRows, rowIndex) }}>
                {row.map((cell, colIndex) => {
                  // A cell a span absorbs is not drawn, as \multicolumn skips it.
                  if (covered.has(`${rowIndex}:${colIndex}`)) return null;
                  const { colspan, rowspan } = cellSpan(cell);
                  const p = plan[colIndex];
                  const planned = planCell(p, cellText(cell));
                  const common = {
                    "data-testid": testIds.table.cell,
                    "data-row": rowIndex,
                    "data-col": colIndex,
                    "data-best": planned.best || undefined,
                    colSpan: colspan > 1 ? colspan : undefined,
                    rowSpan: rowspan > 1 ? rowspan : undefined,
                    style: { padding: "8px 12px", textAlign: p?.decimal ? "center" : undefined } as React.CSSProperties,
                  };
                  const mark = layout.marks.get(`${rowIndex}:${colIndex}`);
                  if (planned.number && p)
                    return <td key={colIndex} {...common}><DecimalNumber n={planned.number} plan={p} bold={planned.best} /><NoteMark mark={mark} /></td>;
                  const html = renderMarkersToHtml(planned.text);
                  return (
                    <td key={colIndex} {...common} dangerouslySetInnerHTML={{ __html: (planned.best ? `<strong>${html}</strong>` : html) + noteMarkHtml(mark) }} />
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {/* The notes under the table, as threeparttable prints them. */}
        <TableNotes notes={layout.notes} style={{ padding: "0 4px", color: "var(--lilia-text, #334155)" }} />
        <div className={styles.tableActions}>
          <button onClick={handleOpenModal} className={styles.tableButton} data-testid={testIds.table.edit}>Edit Table</button>
          {editor?.isEditable !== false && documentId && tableBlockId && (
            // Olivia (README-step4, 4a review): tonal; when it can't plot it stays focusable
            // (aria-disabled), says why to screen readers, and a tap shows the reason.
            <span className={styles.tableActionWrap}>
              <button
                type="button"
                className={styles.tableButtonTonal}
                data-testid={testIds.plot.tableMenuPlot}
                data-disabled={cantPlot ? "true" : undefined}
                aria-disabled={cantPlot ? true : undefined}
                aria-describedby={cantPlot ? `plot-reason-${tableBlockId}` : undefined}
                title={cantPlot ?? "Plot this table: a chart that follows it"}
                onClick={() => (cantPlot ? setShowPlotReason((v) => !v) : setPlotOpen(true))}
                onBlur={() => setShowPlotReason(false)}
              >
                Plot this table
              </button>
              {cantPlot && (
                <span
                  id={`plot-reason-${tableBlockId}`}
                  role="tooltip"
                  data-testid={testIds.plot.tableMenuPlotReason}
                  className={showPlotReason ? styles.tableActionTip : styles.visuallyHidden}
                >
                  {cantPlot}
                </span>
              )}
            </span>
          )}
        </div>
        {plotOpen && documentId && !isPlotProblem(suggested) && (
          <PlotDialog
            fetchWithAuth={fetchWithAuth}
            documentId={documentId}
            tableBlockId={tableBlockId}
            table={plotTable}
            tableName={plotName}
            initial={suggested}
            onClose={() => setPlotOpen(false)}
          />
        )}
      </div>

      <BlockEditModal
        sourceNode={node}
        title="Edit Table"
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        onDelete={() => { setIsModalOpen(false); deleteNode(); }}
        width={860}
      >
        {/* Caption + short caption + label */}
        <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 300px" }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Caption</label>
            <input
              type="text"
              data-testid={testIds.table.editCaption}
              value={draftCaption}
              onChange={(e) => setDraftCaption(e.target.value)}
              placeholder="Table caption..."
              style={{
                width: "100%", padding: "6px 10px", fontSize: 13,
                border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4,
              }}
            />
          </div>
          <div style={{ flex: "1 1 200px" }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Short caption (List of Tables)</label>
            <input
              type="text"
              value={draftShortCaption}
              onChange={(e) => setDraftShortCaption(e.target.value)}
              placeholder="Optional shorter title"
              style={{
                width: "100%", padding: "6px 10px", fontSize: 13,
                border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4,
              }}
            />
          </div>
          <div style={{ flex: "0 0 240px" }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Label</label>
            <LabelChip
              value={draftLabel}
              derived={derivedLabel}
              others={otherLabels}
              onChange={setDraftLabel}
            />
          </div>
        </div>

        <FullWidthSwitch value={draftFullWidth} onChange={setDraftFullWidth} />

        <div style={{ overflowX: "auto" }}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th style={{ width: 32, background: "transparent", border: "none", padding: 0 }} />
                {draftHeaders.map((header, index) => (
                  <th key={index}>
                    <div className={styles.columnReorderBar}>
                      <button type="button" data-testid={testIds.table.editRemoveColumn} data-col={index} className={styles.reorderBtn} onClick={() => removeColumn(index)} title="Remove column" style={{ color: "var(--lilia-text-danger, #dc3545)" }}>
                        <IconTrash size={12} />
                      </button>
                    </div>
                    <div className={styles.cellWrapper}>
                      <input type="text" data-testid={testIds.table.editHeader} data-col={index} value={cellText(header)} onChange={(e) => handleHeaderChange(index, e.target.value)} className={styles.tableInput} />
                    </div>
                    {/* Per-column alignment + (when wrap is selected) width.
                        Width is only meaningful for "p" — for l/c/r the
                        input is hidden to keep the header compact. */}
                    <div style={{ display: "flex", gap: 4, marginTop: 4, fontSize: 11 }}>
                      <select
                        value={(draftColumnAlign[index] as ColAlign) || "l"}
                        onChange={(e) => setColumnAlign(index, e.target.value)}
                        title="Column alignment"
                        style={{ flex: 1, padding: "2px 4px", fontSize: 11, border: "1px solid var(--lilia-border, #ddd)", borderRadius: 3 }}
                      >
                        {COL_ALIGN_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      {(draftColumnAlign[index] || "l") === "p" && (
                        <input
                          type="text"
                          value={draftColumnWidth[index] || ""}
                          onChange={(e) => setColumnWidth(index, e.target.value)}
                          placeholder="6cm"
                          title="Column width (cm/in/pt)"
                          style={{ width: 60, padding: "2px 4px", fontSize: 11, border: "1px solid var(--lilia-border, #ddd)", borderRadius: 3 }}
                        />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {draftRows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  <td style={{ border: "none", width: 32, padding: "0 2px", verticalAlign: "middle" }}>
                    <div className={styles.rowReorderBar}>
                      <button type="button" data-testid={testIds.table.editRemoveRow} data-row={rowIndex} className={styles.reorderBtn} onClick={() => removeRow(rowIndex)} title="Remove row" style={{ color: "var(--lilia-text-danger, #dc3545)" }}>
                        <IconTrash size={12} />
                      </button>
                    </div>
                  </td>
                  {row.map((cell, colIndex) => (
                    <td key={colIndex}>
                      <div className={styles.cellWrapper}>
                        <input type="text" data-testid={testIds.table.editCell} data-row={rowIndex} data-col={colIndex} value={cellText(cell)} onChange={(e) => handleCellChange(rowIndex, colIndex, e.target.value)} className={styles.tableInput} />
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={styles.tableActions}>
          <button onClick={addRow} className={styles.tableButton} data-testid={testIds.table.editAddRow}>+ Add Row</button>
          <button onClick={addColumn} className={styles.tableButton} data-testid={testIds.table.editAddColumn}>+ Add Column</button>
        </div>
      </BlockEditModal>
    </NodeViewWrapper>
  );
}
